import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { displayCompanyName } from '../../utils/displayName';

// Searchable + creatable client picker shared by the Quotation / Invoice / Receipt forms.
// Replaces the plain <select> (unusable with hundreds of clients): type to filter by company
// name / contact name / tax ID, and if nothing matches, open a "New Client" modal (same look as
// Add Contact) to create the client with full details — on save the search closes and the new
// client is auto-selected.

export type ClientOpt = { id: string; company_name: string; client_name?: string; company_name_th?: string; tax_id?: string };

const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 8, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
// Add-Contact-style fields (matches src/components/Contact)
const fLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const fInput: React.CSSProperties = { width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
const fBtn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });

// The company shows without its legal form, "A4", not "A4 Co., Ltd." (Calem, report fcc6685f: "Remove
// any remaining co-limiteds or partnership limiteds from company names in the system"). Label only —
// the pick is the client's id, the search reads the names as filed, and a quotation, invoice or
// receipt takes the full legal name from the client file.
export const clientLabel = (c: ClientOpt): string => {
  const co = c.company_name ? displayCompanyName(c.company_name) : c.company_name;
  return (c.client_name && c.client_name !== c.company_name
    ? (co ? `${co} · ${c.client_name}` : c.client_name)   // individual (no company) → just the name, no leading "·"
    : co) +
  (c.tax_id ? ` · ${c.tax_id}` : '');
};

export default function ClientPicker({ clients, value, onPick, firm, onCreated, disabled, placeholderEmpty }: {
  clients: ClientOpt[];
  value: string;                                   // selected client id ('' = none)
  onPick: (id: string) => void;                    // select an existing/new client, or '' to clear
  firm: 'legal' | 'accounting';                    // firm tag applied to a newly-created client
  onCreated: (c: ClientOpt) => void;               // parent appends the new client to its list
  disabled?: boolean;
  placeholderEmpty?: string;                        // shown when nothing is selected (e.g. a linked receipt)
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);    // the "New Client" modal
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);

  const selected = clients.find(c => c.id === value) || null;

  // anchor the fixed overlay under the trigger (so it's never clipped by the modal's overflow)
  useEffect(() => {
    if (!open) return;
    const recalc = () => { const el = triggerRef.current; if (!el) return; const r = el.getBoundingClientRect(); setRect({ left: r.left, top: r.bottom, width: r.width }); };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [open]);

  // close on outside click (but not while the New Client modal is up)
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (triggerRef.current?.contains(tgt) || boxRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const ql = q.trim().toLowerCase();
  const matches = ql
    ? clients.filter(c => `${c.company_name} ${c.client_name || ''} ${c.tax_id || ''}`.toLowerCase().includes(ql))
    : clients;

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" ref={triggerRef} disabled={disabled}
        onClick={() => { if (!disabled) { setQ(''); setOpen(o => !o); } }}
        style={{ ...inp, textAlign: 'left', cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, opacity: disabled ? 0.6 : 1 }}>
        <span style={{ color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? clientLabel(selected) : (placeholderEmpty || '—')}
        </span>
        <i className="ti ti-chevron-down" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
      </button>

      {open && rect && (
        <div ref={boxRef} style={{ position: 'fixed', left: rect.left, top: rect.top + 2, width: rect.width, zIndex: 10060, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column', maxHeight: 340 }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('cp_search')}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              style={{ ...inp, background: '#fff' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <Opt onClick={() => { onPick(''); setOpen(false); }} muted>—</Opt>
            {matches.slice(0, 80).map(c => (
              <Opt key={c.id} active={c.id === value} onClick={() => { onPick(c.id); setOpen(false); }}>{clientLabel(c)}</Opt>
            ))}
            {matches.length === 0 && <div style={{ padding: '10px', fontSize: 12, color: 'var(--text-tertiary)' }}>{t('cp_none')}</div>}
          </div>
          <button type="button" onClick={() => setShowNew(true)}
            style={{ borderTop: '1px solid var(--border)', padding: '10px', background: '#fff', color: 'var(--accent)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', textAlign: 'left' }}>
            ＋ {t('cp_create')}{q.trim() ? `: “${q.trim()}”` : ''}
          </button>
        </div>
      )}

      {showNew && (
        <NewClientModal initialName={q.trim()} firm={firm}
          onClose={() => setShowNew(false)}
          onCreated={(c) => { onCreated(c); onPick(c.id); setShowNew(false); setOpen(false); setQ(''); }} />
      )}
    </div>
  );
}

function Opt({ children, onClick, active, muted }: { children: React.ReactNode; onClick: () => void; active?: boolean; muted?: boolean }) {
  return (
    <div onClick={onClick}
      style={{ padding: '8px 10px', fontSize: 12.5, cursor: 'pointer', color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)', fontWeight: active ? 700 : 500, background: active ? 'var(--bg-active)' : '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-active)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}>
      {children}
    </div>
  );
}

// New-client modal — same look as the Add Contact dialog, fields mapped to client columns. The firm
// is fixed to the form's selected company so the new client shows up in this picker after creation.
function NewClientModal({ initialName, firm, onClose, onCreated }: {
  initialName: string;
  firm: 'legal' | 'accounting';
  onClose: () => void;
  onCreated: (c: ClientOpt) => void;
}) {
  const { t } = useTranslation();
  // A client is either a juristic person or a natural person, and the answer decides what the system
  // may keep on it (shareholders, employees, a VAT certificate) and which name goes on the document.
  // Guessing it from whether two name fields happen to differ is what mislabelled 23 real people.
  const [entityType, setEntityType] = useState<'company' | 'individual'>('company');
  const [dups, setDups] = useState<any[]>([]);
  // The contact card the name gate refused us for — rendered with a one-click "use it" action.
  const [nameDup, setNameDup] = useState<{ id: string; name: string } | null>(null);
  // A HARD duplicate is a matching tax ID / ID number. "Create anyway" cannot honour its own label
  // there (owner 2026-09-17): the save would link to the EXISTING client and then overwrite that
  // client's name, email, phone and address with what was typed here — silently renaming somebody
  // else's client. The button is withheld for hard matches; the way past one is a different tax ID.
  const [hardDup, setHardDup] = useState(false);
  const [company, setCompany] = useState(initialName);
  // Four-part person name (owner 2026-09-16): documents are built from First + Last, so every
  // create-client surface collects the parts, not one free-text string. For an INDIVIDUAL Title,
  // First and Last are required; a COMPANY requires its company name, the person is optional.
  const [namePrefix, setNamePrefix] = useState('');
  const [nameFirst, setNameFirst] = useState('');
  const [nameMiddle, setNameMiddle] = useState('');
  const [nameLast, setNameLast] = useState('');
  const assembled = [namePrefix, nameFirst, nameMiddle, nameLast].map(v => v.trim()).filter(Boolean).join(' ');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [taxId, setTaxId] = useState('');
  const [branchNo, setBranchNo] = useState('');
  const [address, setAddress] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // `ignoreDup` = the operator looked at the list and says it really is a different client (an
  // agent's mailbox legitimately covers many clients) — owner 2026-08-29.
  async function save(ignoreDup = false) {
    // Owner 2026-09-16: a company must have its company name; an individual must have
    // Title + First + Last — the parts documents are built from.
    if (entityType === 'company' && !company.trim()) { setErr(t('ct_need_company_name')); return; }
    if (entityType === 'individual' && (!namePrefix.trim() || !nameFirst.trim() || !nameLast.trim())) { setErr(t('ct_need_person_name')); return; }
    setSaving(true); setErr(''); setDups([]);
    const isCo = entityType === 'company';
    try {
      // Refuse before creating anything when this tax ID / ID number is already on file. The operator
      // is shown the record that exists and can select it in one click, instead of quietly starting a
      // second file for one company — which is how one client's tax data ended up split in two.
      const chk: any = ignoreDup ? null : await api.clientDuplicateCheck({
        taxId: isCo ? taxId.trim() : '', companyName: company.trim(), clientName: assembled, email: email.trim(),
      }).catch(() => null);
      // SOFT matches (same email, or the same name once spacing is ignored) now stop here too:
      // "Ursula Mijacevic" / "Ursula  Mijacevic" were one person filed twice, and each half got its
      // own receipt for the same ฿2,675.
      const hits = [...(chk?.hard || []), ...(chk?.soft || [])];
      if (hits.length) { setDups(hits); setHardDup((chk?.hard || []).length > 0); setSaving(false); return; }
      // Create a firm-tagged, SHARED (public) CONTACT — the server auto-promotes a Legal/Accounting
      // contact into a linked client record. This keeps the two in sync: the new entry shows up in
      // the Contact module AND is immediately selectable here. (Creating a bare client left it
      // invisible in Contact — the "out of sync" the user hit.)
      // An individual is filed under their own name with NO company attached — owner 2026-08-12
      // ("如果是个人客户,可以不用关联公司"). Sending a company here is what makes a person look like a firm.
      const ct: any = await api.createContact({
        // A company's NAME is the company; the four parts ride along as its contact person only
        // (owner 2026-09-16 review — sending the person as `name` destroyed the company name).
        name: isCo ? company.trim() : assembled, company: isCo ? company.trim() : '', email: email.trim(), phone: phone.trim(),
        name_prefix: namePrefix.trim(), name_first: nameFirst.trim(), name_middle: nameMiddle.trim(), name_last: nameLast.trim(),
        tax_id: isCo ? taxId.trim() : '', branch_no: isCo ? (branchNo.trim() || '00000') : '', address: address.trim(),
        visibility: 'public', source_label: firm === 'legal' ? 'SLF Legal' : 'SLF Accounting',
        entity_type: entityType,
      });
      if (!ct?.client_id) throw new Error('client link not created');
      onCreated({ id: ct.client_id, company_name: isCo ? company.trim() : '', client_name: isCo ? assembled : assembled, tax_id: taxId.trim() });
    } catch (e: any) {
      // A 409 from the contacts name gate names the card that already exists — offer it instead of
      // dead-ending the operator, who otherwise cannot raise the document at all (owner 2026-09-16).
      const dup = e?.status === 409 ? e?.payload?.duplicate : null;
      if (dup?.id) setNameDup(dup);
      setErr(e?.message || 'create failed'); setSaving(false);
    }
  }

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10100 }}>
      <div style={{ width: 480, maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{t('cp_create')}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 18 }}>
          {t('cp_will_add')} <b style={{ color: firm === 'legal' ? '#2B6CB0' : '#1E9E63' }}>{firm === 'legal' ? 'SLF Legal' : 'SLF Accounting'}</b>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={fLabel}>{t('ct_entity')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['company', 'individual'] as const).map(v => (
                <button key={v} type="button" onClick={() => {
                  // The search text lands in `company` by default; for a person it belongs in the
                  // name parts — two tokens split cleanly, anything else goes into First to fix.
                  if (v === 'individual' && !nameFirst.trim() && !nameLast.trim() && company.trim()) {
                    const toks = company.trim().split(/\s+/);
                    setNameFirst(toks[0] || ''); setNameLast(toks.length > 1 ? toks[toks.length - 1] : '');
                    setNameMiddle(toks.slice(1, -1).join(' ')); setCompany('');
                  }
                  setEntityType(v);
                }}
                  style={{ flex: 1, padding: '9px 12px', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    border: `1px solid ${entityType === v ? '#1E9E63' : 'var(--border)'}`,
                    background: entityType === v ? '#E7F6EE' : 'var(--bg-surface)',
                    color: entityType === v ? '#1E9E63' : 'var(--text-secondary)' }}>
                  <i className={`ti ti-${v === 'company' ? 'building' : 'user'}`} style={{ marginRight: 5 }} />
                  {t(v === 'company' ? 'ct_entity_company' : 'ct_entity_individual')}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 5 }}>{t('cp_entity_hint')}</div>
          </div>
          {entityType === 'company' && (
            <div><label style={fLabel}>{t('ct_company_name')} *</label>
              <input autoFocus value={company} onChange={e => setCompany(e.target.value)} style={fInput} placeholder="Acme Co., Ltd" /></div>
          )}
          {/* The person, in four parts — required for an individual; a company's contact, optional. */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 70 }}><label style={fLabel}>{t('cl_np')}{entityType === 'individual' ? ' *' : ''}</label>
              <input autoFocus={entityType === 'individual'} value={namePrefix} onChange={e => setNamePrefix(e.target.value)} style={fInput} list="cp-np-opts" placeholder="Mr." />
              <datalist id="cp-np-opts"><option value="Mr." /><option value="Mrs." /><option value="Ms." /><option value="Miss" /><option value="Dr." /><option value="Khun" /></datalist></div>
            <div style={{ flex: 1 }}><label style={fLabel}>{t('cl_nf')}{entityType === 'individual' ? ' *' : ''}</label>
              <input value={nameFirst} onChange={e => setNameFirst(e.target.value)} style={fInput} /></div>
            <div style={{ flex: 1 }}><label style={fLabel}>{t('cl_nm')}</label>
              <input value={nameMiddle} onChange={e => setNameMiddle(e.target.value)} style={fInput} /></div>
            <div style={{ flex: 1 }}><label style={fLabel}>{t('cl_nl')}{entityType === 'individual' ? ' *' : ''}</label>
              <input value={nameLast} onChange={e => setNameLast(e.target.value)} style={fInput} /></div>
          </div>
          <div><label style={fLabel}>{t('ct_email_addr')}</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={fInput} placeholder="jane@example.com" /></div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><label style={fLabel}>{t('ct_phone')}</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} style={fInput} placeholder="+66 81 234 5678" /></div>
            {entityType === 'company' && (
              <div style={{ flex: 1 }}><label style={fLabel}>{t('ct_branch')}</label>
                <input value={branchNo} onChange={e => setBranchNo(e.target.value)} style={fInput} placeholder="00000" /></div>
            )}
          </div>
          {/* Juristic-person paperwork — an individual has no tax ID here, matching the Contact
              card (owner 2026-09-17). */}
          {entityType === 'company' && (
            <div><label style={fLabel}>{t('ct_tax_id')}</label>
              <input value={taxId} onChange={e => setTaxId(e.target.value)} style={fInput} placeholder="0105551234567" /></div>
          )}
          <div><label style={fLabel}>{t('ct_address')}</label>
            <textarea value={address} onChange={e => setAddress(e.target.value)} rows={2} style={{ ...fInput, resize: 'vertical', lineHeight: 1.6 }} placeholder="123 Moo 4, …" /></div>

          {nameDup && (
            <div style={{ border: '1px solid #FCD34D', background: '#FEF3C7', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#B45309', marginBottom: 6 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} />{nameDup.name}
              </div>
              <button type="button" onClick={async () => {
                try {
                  const c: any = await api.getContact(nameDup.id);
                  if (c?.client_id) { onCreated({ id: c.client_id, company_name: c.company || '', client_name: c.name || '', tax_id: c.tax_id || '' }); return; }
                  setErr(t('cp_dup_no_client'));
                } catch (ex: any) { setErr(ex?.message || 'lookup failed'); }
              }}
                style={{ padding: '5px 11px', borderRadius: 7, border: '1px solid #1E9E63', background: '#fff', color: '#1E9E63', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>
                {t('cp_dup_use')}
              </button>
            </div>
          )}

          {dups.length > 0 && (
            <div style={{ border: '1px solid #FCA5A5', background: '#FEF2F2', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#B91C1C', marginBottom: 6 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} />{t('cp_dup_title')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {dups.map((d: any) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, color: '#7F1D1D' }}>
                      <b>{d.company_name || d.client_name}</b>{d.tax_id ? ` · ${d.tax_id}` : ''}
                      {d.reason ? ` · ${t(`dup_reason_${d.reason}`)}` : ''}
                      {d.active === 0 ? ` · ${t('dup_inactive')}` : ''}
                    </span>
                    {d.active !== 0 && (
                      <button type="button" onClick={() => onCreated({ id: d.id, company_name: d.company_name, client_name: d.client_name, tax_id: d.tax_id })}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #1E9E63', background: '#fff', color: '#1E9E63', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>
                        {t('cp_dup_use')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#7F1D1D', marginTop: 7 }}>{t('cp_dup_hint')}</div>
              {hardDup ? (
                <div style={{ fontSize: 11.5, color: '#7F1D1D', marginTop: 8, fontWeight: 700 }}>
                  <i className="ti ti-lock" style={{ marginRight: 4 }} />{t('cp_dup_hard_locked')}
                </div>
              ) : (
                <button type="button" onClick={() => save(true)} disabled={saving}
                  style={{ marginTop: 8, padding: '5px 11px', borderRadius: 7, border: '1px solid #FCA5A5', background: '#fff', color: '#B91C1C', fontWeight: 700, fontSize: 11.5, cursor: saving ? 'default' : 'pointer' }}>
                  <i className={`ti ti-${saving ? 'loader' : 'plus'}`} style={{ marginRight: 4 }} />{t('dup_create_anyway')}
                </button>
              )}
            </div>
          )}
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} style={fBtn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
            <button onClick={() => save()} disabled={saving} style={{ ...fBtn('var(--accent)'), opacity: saving ? 0.7 : 1 }}>{saving ? t('cp_creating') : t('save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
